<script setup lang="ts">
const api = useApi()
const bridge = useNativeBridge()
const { user, refreshBalance, setBalance } = useAuth()
const { ensureStarted, teardown } = useChat()
// which surfaces this host serves (CASH_ENABLED) — resolved here, before any
// page's setup, so every gate below renders server-side with no flash of a
// cash surface. Web AND native: the footer's cash/RG cluster hangs off it.
const { load: loadFeatures, cashEnabled } = useFeatures()
await loadFeatures()

const isNative = bridge.isNative
const insets = computed(() => bridge.ctx.value.insets)
// Apply native insets only AFTER mount: the injected values arrive client-side,
// so emitting them during hydration would diverge the root's style from the SSR
// HTML. Until then the CSS env() fallback covers the safe area.
const mounted = ref(false)
const safeStyle = computed(() => (isNative.value && mounted.value) ? {
  '--sa-top': `${insets.value.top}px`,
  '--sa-right': `${insets.value.right}px`,
  '--sa-bottom': `${insets.value.bottom}px`,
  '--sa-left': `${insets.value.left}px`,
  // height occluded by the native floating tab bar; folded into the bottom inset
  // (main.css --bt-bottom-safe) so page content, scrollers and bottom-pinned UI
  // clear the bar while the WebView stays full-bleed behind it.
  '--bt-chrome-bottom': `${bridge.ctx.value.obstructedBottom}px`,
} : {})

// Footer only matters off-embed; skip the /version call entirely in the shell.
const { data: ver } = isNative.value
  ? { data: ref<{ version: string } | null>(null) }
  : await useAsyncData('version', () => api.get<{ version: string }>('/version').catch(() => ({ version: '' })))

onMounted(() => {
  if (!isNative.value) return
  mounted.value = true   // now safe to apply injected insets (post-hydration)
  bridge.ready({ appVersion: bridge.ctx.value.appVersion })

  // SSR resolved auth from the probook_session the bridge-login set. If it's
  // absent here, the bridge session lapsed — ask native to re-run the handshake
  // (reload the /api/auth/bridge entry) rather than showing /login.
  if (!user.value) bridge.authRequired('no-session')

  // App lifecycle: drop the chat SSE when backgrounded, resume + reconcile
  // balance when foregrounded.
  bridge.on('LIFECYCLE', (p: { state: string }) => {
    if (p?.state === 'background') teardown()
    else if (p?.state === 'active') { if (user.value) { ensureStarted(); refreshBalance() } }
  })
  // Native signed out: tear everything down, stop calling the API. No redirect.
  // Also retire our session (row + cookie) best-effort — the jar is shared
  // across bets logins, so probook_session must not outlive the bets sign-out
  // (BB-951). Native clears the cookie too; either side succeeding is enough.
  bridge.on('SESSION_INVALIDATED', () => {
    teardown()
    user.value = null
    api.post('/auth/logout').catch(() => {})
  })
  // Native asks for fresh data (e.g. after a wallet top-up).
  bridge.on('REFRESH', (p: { scope?: string }) => {
    refreshBalance()
    if (p?.scope === 'all') reloadNuxtApp({ persistState: false })
  })
  // App-side BMO balance changed → update ProBook's displayed balance directly.
  bridge.on('SET_BALANCE', (p: { available_balance?: number }) => {
    if (typeof p?.available_balance === 'number') setBalance(p.available_balance)
  })
  // Deep link / native tab switch.
  bridge.on('NAV_TO', (p: { path?: string }) => { if (p?.path) navigateTo(p.path) })
})
</script>

<template>
  <div class="app-root" :class="{ embedded: isNative }" :style="safeStyle">
    <slot />
    <!-- footer shows in the native embed too: the RG / helpline obligations apply on
         every surface. version + build mark stay web-only (skipped/uncalled in embed).
         The cash/RG cluster (18+ notice, responsible gaming, helpline) exists for the
         cash product and goes with it when CASH_ENABLED=0 — Terms + Privacy stay. -->
    <footer class="app-footer" :class="{ embedded: isNative }">
      <template v-if="!isNative">
        <span class="mark">BUZZTIME · BMO PROBOOK</span>
        <span class="ver">v{{ ver?.version || '—' }}</span>
        <span class="sep">·</span>
      </template>
      <template v-if="cashEnabled">
        <span class="rg">Cash contests 18+</span>
        <span class="sep">·</span>
        <NuxtLink class="rg-link" to="/account/responsible-gaming">Responsible Gaming</NuxtLink>
        <span class="sep">·</span>
        <a class="rg-link" href="tel:18004262537">1-800-GAMBLER</a>
        <span class="sep">·</span>
      </template>
      <NuxtLink class="rg-link" to="/legal/terms">Terms</NuxtLink>
      <span class="sep">·</span>
      <NuxtLink class="rg-link" to="/legal/privacy">Privacy</NuxtLink>
    </footer>
    <ToastHost />
    <ChatOverlay />
  </div>
</template>

<style scoped>
.app-root { min-height: 100dvh; display: flex; flex-direction: column; }
.app-footer {
  margin-top: auto; display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  padding: 18px 20px; border-top: 1px solid var(--line);
  font-family: var(--font-mono); font-size: 11px; color: var(--muted);
}
.app-footer .mark { letter-spacing: 0.12em; }
.app-footer .ver { color: var(--accent); }
.app-footer .sep { opacity: 0.4; }
.app-footer .rg-link { color: var(--muted); text-decoration: none; }
.app-footer .rg-link:hover { color: var(--ink); text-decoration: underline; }
/* embed: center the compliance line + clear the native floating tab bar */
.app-footer.embedded { justify-content: center; padding-bottom: max(18px, var(--bt-bottom-safe, 0px)); }
</style>
